import React, { useEffect, useState } from 'react';
import { Search, FileSpreadsheet } from 'lucide-react';
import DefaultLayout from '../../../layout/DefaultLayout';
import BreadcrumbTwo from '../../../components/Breadcrumbs/BreadcrumbTwo';
import SelectField from '../../../components/Forms/Input/SelectField';
import DatePicker from '../../../components/Forms/DatePicker/DatePicker';
import ActionButton from '../../../components/Buttons/ActionButton';
import ProductReportGroupService from '../../../services/report/ProductReportGroupService';
import DailyGroupSalesReportService from '../../../services/report/DailyGroupSalesReportService';
import ReportExportService from '../../../services/report/ReportExportService';
import { REPORT_CODES } from '../../../features/reports/reportCodes';
import { useBranch } from '../../../hooks/branches/useBranch';
import useApiErrorHandler from '../../../hooks/useApiErrorHandler';
import { formatCurrency } from '../../../utils/formatCurrency';

interface GroupOption {
  id: number;
  name: string;
  status: boolean;
}

interface ResultRow {
  productId: number;
  productCode: string;
  productName: string;
  unitCode: string | null;
  quantitySold: number;
  totalAmount: number;
}

const today = () => new Date().toISOString().slice(0, 10);

const DailyGroupSalesReportPage: React.FC = () => {
  const { fetchBranchDropdown, branchDropdown } = useBranch();
  const { handle } = useApiErrorHandler();

  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [groupId, setGroupId] = useState<string | number>('');
  const [branchId, setBranchId] = useState<string | number>('');
  const [date, setDate] = useState(today());

  const [result, setResult] = useState<{
    groupName: string;
    rows: ResultRow[];
    totalQuantitySold: number;
    totalAmount: number;
  } | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    fetchBranchDropdown();
    ProductReportGroupService.getActive()
      .then((data) => setGroups(data ?? []))
      .catch((error) => handle(error));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = async () => {
    if (!groupId || !date) return;
    try {
      const data = await DailyGroupSalesReportService.search({
        groupId: Number(groupId),
        branchId: branchId ? Number(branchId) : undefined,
        date,
      });
      setResult(data);
      setHasSearched(true);
    } catch (error: any) {
      handle(error);
    }
  };

  const handleExport = async () => {
    if (!groupId || !date) return;
    setIsExporting(true);
    try {
      await ReportExportService.exportReport({
        reportCode: REPORT_CODES.VENTAS_DIARIAS_GRUPO,
        filters: {
          groupId: Number(groupId),
          branchId: branchId ? Number(branchId) : null,
          date,
        },
      });
    } catch (error: any) {
      handle(error);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DefaultLayout>
      <BreadcrumbTwo />

      <div className="mb-5 rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          <SelectField
            label="Grupo de Productos"
            name="groupId"
            value={groupId}
            onChange={(e) => setGroupId(e.target.value)}
            options={[
              { value: '', label: 'Seleccione un grupo...' },
              ...groups.map((g) => ({ value: g.id, label: g.name })),
            ]}
            iconKey="search"
          />

          <SelectField
            label="Sucursal"
            name="branchId"
            value={branchId}
            onChange={(e) => setBranchId(e.target.value)}
            options={[
              { value: '', label: 'Todas' },
              ...branchDropdown.map((b: any) => ({ value: b.id, label: b.name })),
            ]}
            iconKey="branch"
          />

          <DatePicker
            label="Fecha"
            name="date"
            value={date}
            onChange={(value) => setDate(value)}
            iconKey="calendar"
            withMargin={false}
          />

          <div>
            <label className="mb-2.5 block select-none text-sm font-medium text-transparent">
              Acción
            </label>
            <ActionButton
              label="Consultar"
              icon={<Search className="h-4 w-4" />}
              disabled={!groupId || !date}
              className="h-[50px]"
              onClick={handleSearch}
            />
          </div>
        </div>
      </div>

      {hasSearched && result && (
        <div className="rounded-sm border border-stroke bg-white p-5 shadow-default dark:border-strokedark dark:bg-boxdark">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <h3 className="text-lg font-semibold text-black dark:text-white">
              {result.groupName} — {date}
            </h3>

            <ActionButton
              label="Exportar a Excel"
              icon={<FileSpreadsheet className="h-4 w-4" />}
              variant="success"
              size="sm"
              fullWidth={false}
              loading={isExporting}
              loadingLabel="Exportando..."
              disabled={result.rows.length === 0}
              onClick={handleExport}
            />
          </div>

          <div className="overflow-x-auto">
            <table className="w-full table-auto">
              <thead>
                <tr className="bg-gray-2 text-left dark:bg-meta-4">
                  <th className="px-4 py-2 font-medium">Código</th>
                  <th className="px-4 py-2 font-medium">Producto</th>
                  <th className="px-4 py-2 font-medium text-right">Cant. Vendida</th>
                  <th className="px-4 py-2 font-medium text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {result.rows.length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-4 py-4 text-center text-body">
                      Este grupo no tiene productos.
                    </td>
                  </tr>
                )}
                {result.rows.map((row) => (
                  <tr key={row.productId} className="border-t border-stroke dark:border-strokedark">
                    <td className="px-4 py-2">{row.productCode}</td>
                    <td className="px-4 py-2">{row.productName}</td>
                    <td className="px-4 py-2 text-right">
                      <span
                        className={
                          row.quantitySold > 0
                            ? 'font-semibold text-black dark:text-white'
                            : 'text-body'
                        }
                      >
                        {Number(row.quantitySold).toFixed(3)}
                        {row.unitCode ? ` ${row.unitCode}` : ''}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-right">${formatCurrency(row.totalAmount)}</td>
                  </tr>
                ))}
              </tbody>
              {result.rows.length > 0 && (
                <tfoot>
                  <tr className="border-t-2 border-stroke font-semibold dark:border-strokedark">
                    <td className="px-4 py-2" colSpan={2}>
                      Total
                    </td>
                    <td className="px-4 py-2 text-right">{Number(result.totalQuantitySold).toFixed(3)}</td>
                    <td className="px-4 py-2 text-right">
                      ${formatCurrency(result.totalAmount)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      )}
    </DefaultLayout>
  );
};

export default DailyGroupSalesReportPage;
